import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Box, Stack, Typography, Checkbox, TextField, Paper, Button, CircularProgress } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAssignmentContext } from '../context';
import useIssuedManualGroups, {
  ManualGroup,
} from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManualGroups';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import useGroups from 'views/App/Portal/LexID/Groups/_data_sources/useGroups';
import type { Group } from 'views/App/Portal/LexID/Groups/_types/group';
import _ from 'lodash';
import { useConfirm } from 'material-ui-confirm';

interface StepSelectGroupsProps {
  onCancel?: () => void;
  onNext?: () => void;
  manualId: string;
}

const StepSelectGroups: React.FC<StepSelectGroupsProps> = ({ onCancel, onNext, manualId }) => {
  const { selectedGroups, setSelectedGroups } = useAssignmentContext();
  const { currentOrganizationId } = useContext(AppContext);
  const [search, setSearch] = useState('');
  const confirm = useConfirm();

  // If manualId is present, use issued manual groups, otherwise use all groups
  const groupsQuery = manualId
    ? useIssuedManualGroups(manualId, { search: '' }, { enabled: !!manualId })
    : useGroups<Group>(currentOrganizationId, { search: '' }, { enabled: !!currentOrganizationId });

  // Load all pages of groups
  useEffect(() => {
    if (groupsQuery.data?.pages && groupsQuery.hasNextPage) {
      groupsQuery.fetchNextPage();
    }
  }, [groupsQuery.data?.pages, groupsQuery.hasNextPage, groupsQuery.fetchNextPage]);

  // Normalize group type for selection logic and filter based on search
  const groups: ManualGroup[] = useMemo(() => {
    if (!groupsQuery.data || !groupsQuery.data.pages) return [];

    // Map all groups to ManualGroup shape
    const allGroups = groupsQuery.data.pages.flatMap((x) =>
      x.content.map((g: any) => ({
        id: g.id || '',
        name: g.name || '',
        description: g.description || '',
        orgId: g.orgId || '',
      }))
    );

    // Filter groups based on search term
    if (!search) return allGroups;

    const searchLower = search.toLowerCase();
    return allGroups.filter(
      (g) =>
        g.name.toLowerCase().includes(searchLower) ||
        (g.description && g.description.toLowerCase().includes(searchLower))
    );
  }, [groupsQuery.data, search]);

  const isSelected = (group: ManualGroup) => selectedGroups.some((g) => g.id === group.id);

  const handleToggle = (group: ManualGroup) => {
    setSelectedGroups((prev) => (isSelected(group) ? prev.filter((g) => g.id !== group.id) : [...prev, group]));
  };

  const handleCancel = () => {
    confirm({
      title: t('dtbs.assignments.stepper.discardConfirmTitle'),
      description: t('dtbs.assignments.stepper.discardConfirmBody'),
      cancellationText: t('dtbs.assignments.stepper.discardConfirmCancel'),
      confirmationText: t('dtbs.assignments.stepper.discardConfirmConfirm'),
      cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      confirmationButtonProps: { variant: 'contained', color: 'primary' },
    })
      .then(() => {
        if (onCancel) onCancel();
      })
      .catch(() => {});
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '80vh', width: '100%' }}>
      <Paper
        sx={{
          p: 0,
          maxWidth: 900,
          width: '100%',
          mt: 2,
          minHeight: 420,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'grey.200',
          boxShadow: 'none',
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            px: 6,
            py: 4,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
          }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('dtbs.assignments.stepper.searchGroups')}
            size='medium'
            fullWidth
            sx={{ paddingTop: 0 }}
            InputProps={{
              sx: {
                minHeight: 48,
                fontSize: 16,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
              },
            }}
          />
        </Box>
        <Box sx={{ px: 3, py: 2, flex: 1 }}>
          {groupsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {groups.length === 0 ? (
                <Typography color='text.secondary'>{t('dtbs.assignments.stepper.noGroupsFound')}</Typography>
              ) : (
                <Stack spacing={1}>
                  {groups.map((g) => (
                    <Box
                      key={g.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid',
                        borderColor: 'grey.100',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                        backgroundColor: 'background.paper',
                        gap: 2,
                        boxShadow: 'none',
                      }}
                    >
                      <Checkbox
                        checked={isSelected(g)}
                        size='large'
                        sx={{ color: 'success.main', mr: 1.5, '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        onChange={() => handleToggle(g)}
                      />
                      <Typography sx={{ fontWeight: 500, fontSize: 16, flex: 1 }}>{g.name}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant='body2'>
            {t('dtbs.assignments.stepper.step').replace('{{current}}', '1').replace('{{total}}', '3')}
          </Typography>
          <Stack
            direction='row'
            spacing={2}
          >
            <Button
              variant='outlined'
              onClick={handleCancel}
            >
              {t('dtbs.assignments.stepper.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={onNext}
            >
              {t('dtbs.assignments.stepper.continue')}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default StepSelectGroups;
